import React, { useEffect, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts";
import usaJson from "./usa-map-data.json";
import { useMap } from "./useMap";
import styles from "./Map.module.scss";
import { displayCount } from "Shared/Utils/display";
import { palette, rgba } from "Styles/tokens";
type TJsonFormat = Parameters<typeof echarts.registerMap>[1];

const VehicleMapChart: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReactECharts>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);
  const { vehicleSeries, vehiclesMap } = useMap();

  useEffect(() => {
    // Register the USA map
    echarts.registerMap("USA", usaJson as TJsonFormat, {
      Alaska: { left: -131, top: 25, width: 15 },
      Hawaii: { left: -110, top: 28, width: 5 },
      "Puerto Rico": { left: -76, top: 26, width: 2 },
    });

    // Base map configuration
    const baseOptions = {
      geo: {
        map: "USA",
        roam: false,
        zoom: 1.3,
        itemStyle: {
          borderColor: palette.black,
          borderWidth: 1,
          color: rgba(palette.brand, 0.5),
        },
      },
      toolbox:
        vehicleSeries.length > 0
          ? {
              show: true,
              orient: "vertical",
              showTitle: "true",
              confine: "true",
              textStyle: {
                overflow: "breakAll",
                width: 40,
              },
            }
          : {},
      tooltip:
        vehicleSeries.length > 0
          ? {
              triggerLineEvent: true,
              trigger: "item",
              triggerOn: "click",
              enterable: true,
              formatter: (params: any) => {
                const data = params.data || {};
                const properties = data.properties || {};
                const count = displayCount(properties.count);
                const vehicleNames = properties.vehicles || [];
                const mappedVehicleNames = vehicleNames
                  .map((vehicle: any) => `<div>${vehicle}</div>`)
                  .join("");
                return `
          <div class="${styles.tooltip}">
            <strong>State:</strong> ${params.seriesName || "Unknown"}<br>
            <strong>Count:</strong> ${count}<br>
            <strong>Vehicles:</strong> <br>
            <div>
              ${mappedVehicleNames}
            </div>
          </div>
        `;
              },
            }
          : {},
      series: vehicleSeries.length > 0 ? vehicleSeries : [],
    };

    setChartOptions(baseOptions);
  }, [vehiclesMap]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let frame = 0;
    const resize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const instance = chartRef.current?.getEchartsInstance();
        if (instance && !instance.isDisposed() && container.clientWidth > 0) {
          instance.resize({ width: container.clientWidth });
        }
      });
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: 0 }}>
      <ReactECharts
        ref={chartRef}
        option={chartOptions || {}}
        style={{ width: "100%" }}
        onChartReady={(instance: echarts.EChartsType) => {
          if (containerRef.current?.clientWidth) {
            instance.resize({ width: containerRef.current.clientWidth });
          }
        }}
      />
    </div>
  );
};

export default VehicleMapChart;
